import type {
  LeagueInfo,
  LeagueProjectionAttributes
} from '@library/types/leagues';
import type { SelectOption, SelectOptions } from '@library/types/utils';
import {
  convertLeagueProjectionAttributesToSelectOptions,
  getGameOptions,
  getGamesTeamOptions
} from '@library/component-utils/filters';
import { useCallback, useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import type { LeagueGames } from '@library/types/games';
import type { ProjectionRelationshipDataType } from '@library/types/projection';
import { RankMode } from '@library/types/rankDrawer';
import { formatWithTimezoneChange } from '@library/utils/dateTime';
import { getTradingModifiedProjections } from '@bo-utils/trading';
import { hasStandAloneOwnAttributes } from '@bo-utils/leagueAttributes';
import { useGetRankDrawerProjectionsQuery } from '@bo-services/rankDrawerApi';
import { useImmer } from 'use-immer';
import { useTradingActions } from '@bo-hooks/trading/useTradingActions';

export function useRankDrawer({
  drawerOpen,
  fromDateString,
  initialStatType = undefined,
  leagueId,
  leagueInfo = undefined,
  statTypes,
  games
}: {
  drawerOpen: boolean;
  fromDateString: string;
  initialStatType?: string;
  leagueId: Array<string>;
  leagueInfo?: LeagueInfo;
  statTypes: Array<LeagueProjectionAttributes>;
  games: LeagueGames;
}) {
  const statOptions: SelectOptions =
    convertLeagueProjectionAttributesToSelectOptions(statTypes ?? []);

  const { gameOptions }: { gameOptions: SelectOptions } = getGameOptions({
    games,
    nlsGameOption: false
  });

  const [selectedGames, setSelectedGames] = useState<Array<string>>([]);
  const [selectedTeams, setSelectedTeams] = useState<Array<string>>([]);

  const teamOptions: SelectOptions = getGamesTeamOptions({
    games,
    selectedGameIds: selectedGames
  });

  const [selectedStatType, setSelectedStatType] = useState<
    ProjectionRelationshipDataType['id']
  >((initialStatType || statOptions?.[0]?.value) ?? '');

  const { data: rankDrawerProjectionsData, isFetching } =
    useGetRankDrawerProjectionsQuery(
      {
        leagueId,
        fromDate: formatWithTimezoneChange(
          DateTime.fromISO(fromDateString),
          'yyyy-LL-dd'
        ),
        statuses: ['pre_game', 'removed', 'suspended'],
        statTypeId: selectedStatType,
        gameIds: selectedGames.length ? selectedGames : undefined,
        teamIds: selectedTeams.length ? selectedTeams : undefined
      },
      { skip: !drawerOpen }
    );

  const [projections, setProjections] = useImmer(
    rankDrawerProjectionsData ?? []
  );

  useEffect(() => {
    if (isFetching) {
      setProjections([]);
    } else {
      setProjections(rankDrawerProjectionsData ?? []);
    }
  }, [isFetching, rankDrawerProjectionsData, setProjections]);

  // bulk rank projections, debounced
  const [startingRank, setStartingRank] = useState<number | undefined>();

  const handleBulkRank = (mode: RankMode = RankMode.STARTING) => {
    if (typeof startingRank === 'number') {
      if (mode === RankMode.STARTING) {
        setProjections((draft) => {
          draft.forEach((projection, index) => {
            projection.rank = startingRank + index;
          });
        });
      } else if (mode === RankMode.COPIED) {
        setProjections((draft) => {
          draft.forEach((projection) => {
            projection.rank = startingRank;
          });
        });
      }
    }
  };

  // reset starting rank on change stat type
  const handleSelectStatType = (option: SelectOption) => {
    setSelectedStatType(option.value);
    setStartingRank(undefined);
  };

  const handleSelectGame = useCallback((values: Array<string>) => {
    setSelectedGames(values);
  }, []);

  const handleSelectTeam = useCallback((values: Array<string>) => {
    setSelectedTeams(values);
  }, []);

  // Update resetRank to use the memoized filter
  const resetRank = () => {
    setProjections(rankDrawerProjectionsData ?? []);
    setStartingRank(undefined);
  };

  const { handlePublishProjections } = useTradingActions();

  const updatedProjectionList = getTradingModifiedProjections({
    initialProjections: rankDrawerProjectionsData ?? [],
    updatedProjections: projections
  });

  const hasChanges = updatedProjectionList.length > 0;

  const hasUndefined = updatedProjectionList.some(
    (updatedProjection) => updatedProjection.rank === undefined
  );

  const showDuration =
    leagueId.length > 1 ||
    hasStandAloneOwnAttributes({ name: leagueInfo?.name });

  const saveDisabled = !hasChanges || hasUndefined;

  const saveProjections = () => {
    handlePublishProjections(leagueId[0], updatedProjectionList).catch(
      () => {}
    );
    setStartingRank(undefined);
  };

  return {
    handleBulkRank,
    handleSelectStatType,
    handleSelectGame,
    handleSelectTeam,
    isFetching,
    projections,
    resetRank,
    saveDisabled,
    saveProjections,
    setProjections,
    selectedStatType,
    selectedGames,
    selectedTeams,
    setStartingRank,
    showDuration,
    startingRank,
    statOptions,
    gameOptions,
    teamOptions
  };
}
